import { useState, useEffect, useRef } from "react";

/**
 * ChatHistorySidebar
 * Slide-in left drawer showing past chat sessions stored in the DB.
 * Props:
 *   isOpen         — boolean, controls visibility
 *   onClose        — fn(), close the sidebar
 *   authToken      — JWT for API calls
 *   currentSessionId — the currently active session ID (or null)
 *   onSelectSession  — fn(session) — called when user clicks a session
 *   onNewChat        — fn() — called when "New Chat" is clicked
 *   onDeleteSession  — fn(sessionId) — called after user confirms delete
 */
export default function ChatHistorySidebar({
  isOpen,
  onClose,
  authToken,
  currentSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  inline = false,   // ← NEW: renders as embedded panel (no fixed overlay)
}) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const sidebarRef = useRef(null);

  // Fetch sessions whenever sidebar opens
  useEffect(() => {
    if (isOpen && authToken) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, authToken]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/chat/sessions", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, sessionId) => {
    e.stopPropagation(); // don't select the session
    if (!window.confirm("Delete this chat session?")) return;
    setDeletingId(sessionId);
    try {
      const res = await fetch(`/api/chat/sessions/${sessionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        if (onDeleteSession) onDeleteSession(sessionId);
      }
    } catch (err) {
      console.error("Delete session failed:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (isoStr) => {
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  // Group sessions by date label
  const grouped = sessions.reduce((acc, session) => {
    const label = formatDate(session.updated_at || session.created_at);
    if (!acc[label]) acc[label] = [];
    acc[label].push(session);
    return acc;
  }, {});

  // ── Inline mode: render as a plain embedded panel ─────────────────────────
  if (inline) {
    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-1)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-0)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🕐</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>Chat History</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{sessions.length} session{sessions.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <button
            onClick={() => { onNewChat(); }}
            style={{ padding: '5px 12px', borderRadius: 8, background: 'var(--brand-gradient)', border: 'none', color: '#fff', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}
          >+ New Chat</button>
        </div>
        {/* Sessions list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 120, gap: 10, color: 'var(--text-muted)' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--brand-primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 11 }}>Loading…</span>
            </div>
          ) : sessions.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, gap: 10, color: 'var(--text-muted)', padding: '0 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, opacity: 0.35 }}>💬</div>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>No saved sessions yet. Start chatting!</div>
            </div>
          ) : (
            Object.entries((() => {
              const g = {};
              sessions.forEach(s => { const l = formatDate(s.updated_at || s.created_at); if (!g[l]) g[l] = []; g[l].push(s); });
              return g;
            })()).map(([label, grp]) => (
              <div key={label}>
                <div style={{ padding: '6px 14px 3px', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                {grp.map(session => {
                  const isActive = session.id === currentSessionId;
                  return (
                    <div
                      key={session.id}
                      onClick={() => { onSelectSession(session); }}
                      className="chat-history-item"
                      style={{ padding: '8px 12px', margin: '2px 8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: isActive ? 'rgba(59,130,246,0.12)' : 'transparent', border: isActive ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent', transition: 'all 0.15s' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--brand-primary)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {session.title || 'Untitled Chat'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                          {new Date(session.updated_at || session.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <button onClick={(e) => handleDelete(e, session.id)} className="chat-history-delete-btn" style={{ background: 'transparent', border: 'none', borderRadius: 5, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, flexShrink: 0, opacity: 0, transition: 'opacity 0.15s' }} title="Delete">🗑</button>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border-default)', fontSize: 10, color: 'var(--text-muted)', textAlign: 'center' }}>Sessions auto-saved</div>
      </div>
      <style>{`
        .chat-history-item:hover {
          background: var(--surface-2) !important;
          border-color: var(--border-default) !important;
        }
        .chat-history-item:hover .chat-history-delete-btn {
          opacity: 1 !important;
        }
        .chat-history-delete-btn:hover {
          background: rgba(239,68,68,0.15) !important;
          color: #ef4444 !important;
        }
      `}</style>
      </>
    );
  }

  // ── Overlay / slide-in mode (original behavior) ──────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        className="chat-history-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 998,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 0.25s ease",
          backdropFilter: "blur(2px)",
        }}
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <div
        ref={sidebarRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: "300px",
          maxWidth: "85vw",
          background: "var(--surface-1)",
          borderRight: "1px solid var(--border-default)",
          zIndex: 999,
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          boxShadow: isOpen ? "4px 0 32px rgba(0,0,0,0.4)" : "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 16px 16px",
            borderBottom: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--surface-0)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--brand-gradient)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 15,
              }}
            >
              🕐
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                Chat History
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                {sessions.length} session{sessions.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "var(--surface-2)",
              border: "1px solid var(--border-default)",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "var(--text-secondary)",
              fontSize: 16,
              transition: "all 0.15s",
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* New Chat button */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <button
            onClick={() => { onNewChat(); onClose(); }}
            style={{
              width: "100%",
              padding: "10px 16px",
              borderRadius: 10,
              background: "var(--brand-gradient)",
              border: "none",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "opacity 0.15s",
            }}
          >
            <span style={{ fontSize: 16 }}>+</span>
            New Chat
          </button>
        </div>

        {/* Sessions list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 160,
                gap: 12,
                color: "var(--text-muted)",
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "2px solid var(--brand-primary)",
                  borderTopColor: "transparent",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <span style={{ fontSize: 12 }}>Loading sessions…</span>
            </div>
          ) : sessions.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                gap: 12,
                color: "var(--text-muted)",
                padding: "0 24px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 40, opacity: 0.4 }}>💬</div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                No saved sessions yet. Start a chat to save your history!
              </div>
            </div>
          ) : (
            Object.entries(grouped).map(([label, groupSessions]) => (
              <div key={label}>
                {/* Date group label */}
                <div
                  style={{
                    padding: "8px 16px 4px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {label}
                </div>

                {groupSessions.map((session) => {
                  const isActive = session.id === currentSessionId;
                  const isDeleting = deletingId === session.id;
                  return (
                    <div
                      key={session.id}
                      onClick={() => { onSelectSession(session); onClose(); }}
                      className="chat-history-item"
                      style={{
                        padding: "10px 16px",
                        margin: "2px 8px",
                        borderRadius: 10,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        background: isActive
                          ? "rgba(59,130,246,0.12)"
                          : "transparent",
                        border: isActive
                          ? "1px solid rgba(59,130,246,0.3)"
                          : "1px solid transparent",
                        transition: "all 0.15s",
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? "var(--brand-primary)" : "var(--text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {session.title || "Untitled Chat"}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {new Date(session.updated_at || session.created_at).toLocaleTimeString(
                            "en-IN",
                            { hour: "2-digit", minute: "2-digit" }
                          )}
                        </div>
                      </div>

                      {/* Delete button (visible on hover) */}
                      <button
                        onClick={(e) => handleDelete(e, session.id)}
                        className="chat-history-delete-btn"
                        style={{
                          background: "transparent",
                          border: "none",
                          borderRadius: 6,
                          width: 26,
                          height: 26,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: "var(--text-muted)",
                          fontSize: 13,
                          flexShrink: 0,
                          opacity: 0,
                          transition: "opacity 0.15s, background 0.15s",
                        }}
                        title="Delete session"
                        disabled={isDeleting}
                      >
                        🗑
                      </button>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--border-default)",
            fontSize: 11,
            color: "var(--text-muted)",
            textAlign: "center",
          }}
        >
          Sessions are saved automatically
        </div>
      </div>

      <style>{`
        .chat-history-item:hover {
          background: var(--surface-2) !important;
          border-color: var(--border-default) !important;
        }
        .chat-history-item:hover .chat-history-delete-btn {
          opacity: 1 !important;
        }
        .chat-history-delete-btn:hover {
          background: rgba(239,68,68,0.15) !important;
          color: #ef4444 !important;
        }
      `}</style>
    </>
  );
}
